// Jog-katalógus — a jog-CÍMKÉK és MODUL-CSOPORTOK mostantól az ODINBÓL jönnek (app-agnosztikus: minden app a
// SAJÁT GAZDAG metaadatát adja — kétnyelvű címke + csoport + sorrend; a konzol az `adminPermissions`-ből
// olvassa, lásd `access-rights.tsx`). Így nincs a kódban app-specifikus jog-szótár, és nincs vegyes forrás.
// Itt CSAK a UNIVERZÁLIS segédek maradnak: a szerep-tier címkéje (member/admin/superadmin — appok közt közös)
// és a feature-kulcs felismerése. A jog-KULCS a szerződés/forrásigazság (sosem fordítjuk).
import i18n from '@/i18n';

/** A szerep-tier (member/admin/superadmin) ember-olvasható címkéje — UNIVERZÁLIS (nem app-specifikus). */
export function roleLabel(key: string): string {
  return i18n.t(`perm.role.${key}`, { defaultValue: key });
}

/** Egy kulcs feature-jog-e (dot-formátum, NEM platform-scope kettősponttal — pl. `platform:admin`). */
export function isFeatureKey(key: string): boolean {
  return key.includes('.') && !key.includes(':');
}

/**
 * KÉPESSÉG-SCOPE-OK, amiket a CONSUMER-konzol (Atlas jog-modal) mégis kezelhet — emberenként pipálhatók.
 *
 * A fő szabály marad: consumer-appnál a szerkeszthető univerzum a FEATURE (pont-)kulcsok halmaza, a
 * colon-scope-ok (`atlas:admin`, `*:write`, `read:all:*`) a platform adat-hozzáférését hordozzák, azokat a
 * konzol nem bolygatja (a BFF megőrzi őket). EZ AZ ALLOWLIST a kivétel, és szűk okból van:
 *
 *   • `iam:impersonate` — a MEGSZEMÉLYESÍTÉS („belépés más nevében"). Ez EMBERI, felületi jog: a jog-konzol
 *     „Adminisztráció" csoportjában egy pipa, amit a jogosultság-kezelő emberenként be- és kikapcsol
 * (user-döntés). Nincs mellette külön feature-jog: a KULCS MAGA az Odin képessége, mert így
 *     nem lehet „bepipálva, de mégsem működik" állapot. Az Odin oldalán a delegálás megengedett annak, aki a
 *     kulcsot maga is birtokolja / `rbac:admin` (lásd `assertCanGrant` + `test/rbac-sync.e2e.spec.ts`).
 *
 *   • `knowledge:admin` — a MORGENS Hub szerep-/jogkezelése. Az „App-jogok kezelése” csoport egy pipája: aki megkapja,
 *     annak az Atlas jog-konzolja a Hubot is kezeli (az app-váltó a szervertől kapott lista hosszából jön —
 *     l. `modules/admin/access-rights.md` §1.1). A kulcs a Hub namespace-ének admin-scope-ja, és
 *     **NEM governance** (az csak a `platform:admin` és az `rbac:admin`), ezért szerephez köthető és nem
 *     ütközik a jelszó-beállító fallal. ÚJ APP → egy új kulcs ebbe a csoportba (apponként egyszer, nem
 *     jogonként).
 *
 * Új tétel ide csak akkor kerülhet, ha (a) a kulcs valóban EMBERI, per-user jog, és (b) az Odin delegálás-fala
 * engedi a consumer-admin kiosztását — különben a pipa 403-ba futna, azaz hamis affordancia lenne.
 */
export const CONSOLE_MANAGEABLE_CAPABILITIES: ReadonlySet<string> = new Set([
  'iam:impersonate',
  'knowledge:admin',
]);

/** A consumer-konzolban SZERKESZTHETŐ kulcs: feature-jog VAGY a fenti allowlist tagja. */
export function isConsoleManageableKey(key: string): boolean {
  return isFeatureKey(key) || CONSOLE_MANAGEABLE_CAPABILITIES.has(key);
}

/** Egy per-user kivétel-sor a jogosultság-konzolból. */
export interface PermissionOverride {
  key: string;
  effect: 'grant' | 'deny';
}

/**
 * A VALÓDI (érdemi) kivételek: azok a per-user sorok, amik TÉNYLEGESEN eltérítik a szerep alapértelmezését.
 *
 * MIÉRT KELL EZ KÜLÖN SZABÁLYKÉNT (mért felhasználói lelet): egy kivétel-sor MEGMARAD akkor is, ha a jog
 * időközben bekerül a SZEREPBE. Ilyenkor a sor létezik, de már nem ad semmit — a szerep amúgy is adja.
 * A lista eddig a NYERS sorokat számolta, a szerkesztő-modál viszont a szerephez KÉPEST — ezért ugyanarra
 * az emberre a lista „1 többlet”-et írt, a modál meg „mindent örökölt”-et. Ugyanaz a fogalom, két
 * számítás: a lista a saját oszlop-címkéjének („többlet”) mondott ellent.
 *
 * A SZABÁLY, egy helyen:
 *   • `grant` csak akkor érdemi, ha a szerep NEM adja (különben redundáns sor),
 *   • `deny` csak akkor érdemi, ha a szerep ADJA (különben nincs mit letiltani).
 */
export function meaningfulOverrides(
  overrides: readonly PermissionOverride[],
  roleDefaults: ReadonlySet<string>,
): { grants: PermissionOverride[]; denies: PermissionOverride[] } {
  return {
    grants: overrides.filter((o) => o.effect === 'grant' && !roleDefaults.has(o.key)),
    denies: overrides.filter((o) => o.effect === 'deny' && roleDefaults.has(o.key)),
  };
}
